function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
import "core-js/modules/es.array.reverse.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, computed, observable, onBecomeObserved, makeObservable, override } from "mobx";
import Cartographic from "terriajs-cesium/Source/Core/Cartographic";
import Ellipsoid from "terriajs-cesium/Source/Core/Ellipsoid";
import JulianDate from "terriajs-cesium/Source/Core/JulianDate";
import CesiumMath from "terriajs-cesium/Source/Core/Math";
import filterOutUndefined from "../Core/filterOutUndefined";
import runLater from "../Core/runLater";
import CommonStrata from "../Models/Definition/CommonStrata";
import createStratumInstance from "../Models/Definition/createStratumInstance";
import TerriaFeature from "../Models/Feature/Feature";
import { TimeFilterCoordinates } from "../Traits/TraitsClasses/TimeFilterTraits";
import DiscretelyTimeVaryingMixin from "./DiscretelyTimeVaryingMixin";
import MappableMixin, { ImageryParts } from "./MappableMixin";
/**
 * A Mixin for filtering the dates for which imagery is available at a location
 * picked by the user
 *
 * When `timeFilterPropertyName` is set, we look for a property of that name in
 * the feature query response at the picked location. The property value should be
 * an array of dates for which imagery is available at the location. This
 * Mixin is used to implement the Location filter feature for Satellite
 * Imagery.
 */
function TimeFilterMixin(Base) {
  var _class, _descriptor;
  let TimeFilterMixin = (_class = class TimeFilterMixin extends DiscretelyTimeVaryingMixin(Base) {
    constructor() {
      super(...arguments);
      _initializerDefineProperty(this, "_currentTimeFilterFeature", _descriptor, this);
      makeObservable(this);

      // Try to resolve the timeFilterFeature from the co-ordinates that might
      // be stored in the traits. We only have to resolve the time filter
      // feature once to get the list of times.
      const disposeListener = onBecomeObserved(this, "mapItems", () => {
        runLater(action(async () => {
          if (!MappableMixin.isMixedInto(this)) {
            disposeListener();
            return;
          }
          const coords = coordinatesFromTraits(this.timeFilterCoordinates);
          if (coords) {
            this.setTimeFilterFromLocation(coords);
          }
          disposeListener();
        }));
      });
    }

    /**
     * Loads the time filter by querying the imagery layers at the given
     * co-ordinates.
     *
     * @param coordinates.position The lat, lon, height of the location to pick in degrees
     * @param coordinates.tileCoords the x, y, level co-ordinates of the tile to pick
     * @returns Promise that fulfills when the picking is complete.
     *   The promise resolves to `false` if the filter could not be set for some reason.
     */
    async setTimeFilterFromLocation(coordinates) {
      const timeProperty = this.timeFilterPropertyName;
      if (this.terria.allowFeatureInfoRequests === false || timeProperty === undefined || !MappableMixin.isMixedInto(this)) {
        return false;
      }
      const pickTileCoordinates = coordinates.tileCoords;
      const pickCartographicPosition = Cartographic.fromDegrees(coordinates.position.longitude, coordinates.position.latitude, coordinates.position.height);

      // Pick all imagery provider for this item
      const imageryProviders = filterOutUndefined(this.mapItems.map(mapItem => ImageryParts.is(mapItem) ? mapItem.imageryProvider : undefined));
      const picks = await pickImageryProviders(imageryProviders, pickTileCoordinates, pickCartographicPosition);

      // Find the first imageryProvider and feature with a matching time property
      let timeFeature;
      let imageryUrl;
      for (let i = 0; i < picks.length; i++) {
        const pick = picks[i];
        const imageryFeature = pick.imageryFeatures.find(f => f.properties[timeProperty] !== undefined);
        imageryUrl = pick.imageryProvider.url;
        if (imageryFeature && imageryUrl) {
          var _imageryFeature$posit;
          imageryFeature.position = (_imageryFeature$posit = imageryFeature.position) !== null && _imageryFeature$posit !== void 0 ? _imageryFeature$posit : pickCartographicPosition;
          timeFeature = TerriaFeature.fromImageryLayerFeatureInfo(imageryFeature);
          break;
        }
      }
      if (!timeFeature || !imageryUrl) {
        return false;
      }

      // Update time filter
      this.setTimeFilterFeature(timeFeature, {
        [imageryUrl]: _objectSpread(_objectSpread({}, coordinates.position), coordinates.tileCoords)
      });
      return true;
    }
    get hasTimeFilterMixin() {
      return true;
    }
    get canFilterTimeByFeature() {
      return this.timeFilterPropertyName !== undefined;
    }
    get imageryUrls() {
      if (!MappableMixin.isMixedInto(this)) return [];
      return filterOutUndefined(this.mapItems.map(
      // @ts-expect-error url attr
      mapItem => ImageryParts.is(mapItem) && mapItem.imageryProvider.url));
    }
    get featureTimesAsJulianDates() {
      var _this$_currentTimeFil;
      if (this._currentTimeFilterFeature === undefined || this._currentTimeFilterFeature.properties === undefined || this.timeFilterPropertyName === undefined) {
        return;
      }
      const featureTimes = (_this$_currentTimeFil = this._currentTimeFilterFeature.properties[this.timeFilterPropertyName]) === null || _this$_currentTimeFil === void 0 ? void 0 : _this$_currentTimeFil.getValue(this.currentTime);
      if (!Array.isArray(featureTimes)) {
        return;
      }
      return filterOutUndefined(featureTimes.map(s => {
        try {
          return s === undefined ? undefined : JulianDate.fromIso8601(s);
        } catch (_unused) {
          return undefined;
        }
      }));
    }
    get discreteTimesAsSortedJulianDates() {
      var _super$discreteTimesA;
      const featureTimes = this.featureTimesAsJulianDates;
      if (featureTimes === undefined) {
        return super.discreteTimesAsSortedJulianDates;
      }
      return (_super$discreteTimesA = super.discreteTimesAsSortedJulianDates) === null || _super$discreteTimesA === void 0 ? void 0 : _super$discreteTimesA.filter(dt => featureTimes.some(d => d.equals(dt.time)));
    }
    get timeFilterFeature() {
      return this._currentTimeFilterFeature;
    }
    setTimeFilterFeature(feature, providerCoords) {
      if (!MappableMixin.isMixedInto(this) || providerCoords === undefined) return;
      this._currentTimeFilterFeature = feature;
      if (!this.currentTimeAsJulianDate) {
        return;
      }
      if (!feature.position) {
        return;
      }
      const position = feature.position.getValue(this.currentTimeAsJulianDate);
      if (position === undefined) return;
      const cartographic = Ellipsoid.WGS84.cartesianToCartographic(position);
      const featureImageryUrl = this.imageryUrls.find(url => providerCoords[url]);
      const tileCoords = featureImageryUrl && providerCoords[featureImageryUrl];
      if (!tileCoords) return;
      this.setTrait(CommonStrata.user, "timeFilterCoordinates", createStratumInstance(TimeFilterCoordinates, {
        tile: tileCoords,
        longitude: CesiumMath.toDegrees(cartographic.longitude),
        latitude: CesiumMath.toDegrees(cartographic.latitude),
        height: cartographic.height
      }));
    }
    removeTimeFilterFeature() {
      this._currentTimeFilterFeature = undefined;
      this.setTrait(CommonStrata.user, "timeFilterCoordinates", undefined);
    }
  }, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_currentTimeFilterFeature", [observable], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _applyDecoratedDescriptor(_class.prototype, "setTimeFilterFromLocation", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setTimeFilterFromLocation"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canFilterTimeByFeature", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canFilterTimeByFeature"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "imageryUrls", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "imageryUrls"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "featureTimesAsJulianDates", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "featureTimesAsJulianDates"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "discreteTimesAsSortedJulianDates", [override], Object.getOwnPropertyDescriptor(_class.prototype, "discreteTimesAsSortedJulianDates"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "timeFilterFeature", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "timeFilterFeature"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setTimeFilterFeature", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setTimeFilterFeature"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeTimeFilterFeature", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeTimeFilterFeature"), _class.prototype), _class);
  return TimeFilterMixin;
}
(function (_TimeFilterMixin2) {
  function isMixedInto(model) {
    return model && model.hasTimeFilterMixin;
  }
  _TimeFilterMixin2.isMixedInto = isMixedInto;
})(TimeFilterMixin || (TimeFilterMixin = {}));
/**
 * Picks all the imagery providers at the given coordinates and return a
 * promise that resolves to the (imageryProvider, imageryFeatures) pairs.
 */
async function pickImageryProviders(imageryProviders, pickCoordinates, pickPosition) {
  return filterOutUndefined(await Promise.all(imageryProviders.map(imageryProvider => {
    var _imageryProvider$pick;
    return (_imageryProvider$pick = imageryProvider.pickFeatures(pickCoordinates.x, pickCoordinates.y, pickCoordinates.level, pickPosition.longitude, pickPosition.latitude)) === null || _imageryProvider$pick === void 0 ? void 0 : _imageryProvider$pick.then(imageryFeatures => ({
      imageryProvider,
      imageryFeatures
    }));
  })));
}
function coordinatesFromTraits(traits) {
  const {
    latitude,
    longitude,
    height,
    tile: {
      x,
      y,
      level
    }
  } = traits;
  if (latitude === undefined || longitude === undefined) return;
  if (x === undefined || y === undefined || level === undefined) return;
  return {
    position: {
      latitude,
      longitude,
      height
    },
    tileCoords: {
      x,
      y,
      level
    }
  };
}
export default TimeFilterMixin;